import { useState, useEffect, useCallback } from "react";
import type { Driver } from "../types/session";
import { apiFetch } from "../apiFetch";

export function useDrivers() {
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDrivers = useCallback(async () => {
    try {
      const res = await apiFetch("/api/drivers");
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setDrivers(data.drivers);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDrivers();
  }, [fetchDrivers]);

  const createDriver = useCallback(async (name: string) => {
    try {
      const res = await apiFetch("/api/drivers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      const driver: Driver = await res.json();
      setDrivers((prev) => [...prev, driver].sort((a, b) => a.name.localeCompare(b.name)));
      return { ok: true as const, driver };
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      return { ok: false as const, error: msg };
    }
  }, []);

  const deleteDriver = useCallback(async (id: number) => {
    try {
      const res = await apiFetch(`/api/drivers/${id}`, { method: "DELETE" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      setDrivers((prev) => prev.filter((d) => d.id !== id));
      return { ok: true as const };
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      return { ok: false as const, error: msg };
    }
  }, []);

  return { drivers, loading, error, createDriver, deleteDriver, refresh: fetchDrivers };
}
